import { FC } from 'react';
import { Location, Profile } from '@one-thd/sui-icons';
import { Typography } from '@one-thd/sui-atomic-components';
import { getPersonName, formatStorePhone, getStorePhoneDigits } from '../../utils/order-details';
import { CityStateZipLine } from './CityStateZipLine';
import { IStorePickup } from '../../models/order-details';

interface IStorePickupDetails {
  storePickup?: IStorePickup;
}

export const StorePickupDetails: FC<IStorePickupDetails> = ({ storePickup }) => {
  const store = storePickup?.store;

  /**
   * With no store there is no address to show: the block would render a bare
   * '#', an empty city line and an empty pickup-person slot. Same guard
   * `DeliveryAddressDetails` applies to a missing `deliveryAddress`.
   */
  if (!store) {
    return null;
  }

  const address = store.address;
  const pickupPersonName = getPersonName(storePickup?.pickUpPerson?.name);

  /** `Cumberland #0509`, or whichever half of it the payload sent. */
  const storeNameAndNumber = [store.name, store.number && `#${store.number}`]
    .filter(Boolean)
    .join(' ');

  /**
   * Undefined for anything that is not a usable ten-digit number, in which case
   * the raw field is rendered unlinked below — an extension, a local
   * seven-digit number or an international number is still the store's phone
   * number, and dropping it entirely loses more than the dialler link does.
   */
  const storePhone = formatStorePhone(store.phone);
  /**
   * The raw field is not guaranteed to be digits (`(770) 555-1234`), so the
   * `tel:` href is built from the same cleaned digits as the display string,
   * not from `store.phone` directly.
   */
  const storePhoneDigits = getStorePhoneDigits(store.phone);
  const rawPhone = store.phone?.trim();
  /** The link's digits do not say what it does, so it names the store it dials. */
  const callLabel = store.number ? `Call store #${store.number}` : 'Call the store';

  return (
    <div data-testid="store-pickup-address-details">
      {/* Pickup At. Store name and number, street, city line, phone — one block. */}
      <div className="sui-flex sui-p-4">
        <div className="sui-flex sui-flex-row sui-gap-4 sui-pl-2">
          <span className="sui-mt-1">
            <Location size="small" />
          </span>
          <div>
            <Typography variant="h6">Pickup At</Typography>

            {storeNameAndNumber && (
              <Typography variant="body-sm">
                {storeNameAndNumber}
              </Typography>
            )}

            {address?.line1 && (
              <Typography variant="body-sm">
                {address.line1}
              </Typography>
            )}

            {address?.line2 && (
              <Typography variant="body-sm">
                {address.line2}
              </Typography>
            )}

            <CityStateZipLine
              city={address?.city}
              state={address?.state}
              zipCode={address?.zipCode}
            />

            {storePhone && storePhoneDigits && (
              <Typography variant="body-sm">
                <a href={`tel:${storePhoneDigits}`} aria-label={callLabel} data-testid="store-phone">
                  {storePhone}
                </a>
              </Typography>
            )}

            {!storePhone && rawPhone && (
              <Typography variant="body-sm" data-testid="store-phone">
                {rawPhone}
              </Typography>
            )}
          </div>
        </div>
      </div>

      <hr className="sui-m-0" />

      {/* Pickup Person */}
      <div className="sui-flex sui-p-4">
        <div className="sui-flex sui-flex-row sui-gap-4 sui-pl-2">
          <span className="sui-mt-1">
            <Profile size="small" />
          </span>
          <div>
            <Typography variant="h6">Pickup Person</Typography>

            {pickupPersonName && (
              <Typography variant="body-sm">
                {pickupPersonName}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
